import React from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useFlow } from '../../../contexts/FlowContext';
import { useWhatsAppConnection } from '../../../contexts/WhatsAppConnectionContext';
import { isBlankFlow, hydrate } from '../../../flow-core/hydrate';
import { isBookingFlowJson } from '../../../flow-core/booking';
import { buildBuilderValidationMap } from '../../../utils/flowValidation';
import { flowTypeOf, inferFlowType } from '../../../utils/flowType';
import { useLiveDataConnected } from '../../../hooks/useLiveDataConnected';
import ValidationSummaryPanel from '../ValidationSummaryPanel';
import flowGenerationService from '../../../services/flowGenerationService';
import flowAssetService from '../../../services/flowAssetService';
import AiFlowBuilder from './AiFlowBuilder';
import { problemsFromValidationMap, fixRequestText } from './fixRequest';
import WhatsAppPreviewDialog from '../WhatsAppPreviewDialog';
import FlowConnectionsDrawer from '../../flows/FlowConnectionsDrawer';
import SendToPhoneDialog from '../SendToPhoneDialog';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { jsonEquals } from '../../../utils/jsonEquals';
import { useAutoSavePreference } from '../../../hooks/useAutoSavePreference';
import { useDirtyState } from '../../../hooks/useDirtyState';
import { loadDraft, saveDraft, clearDraft } from '../../../utils/assistantDraft';
import { useFlowSyncStatus } from '../../../hooks/useFlowSyncStatus';
import { track } from '../../../utils/analytics';
import { markFirstBrief, markFlowBuilt } from '../../../utils/funnelJourney';

/**
 * Owns the editing conversation; {@link AiFlowBuilder} draws it.
 *
 * Every request carries the flow AS IT IS NOW, read from the canvas rather than from the last
 * saved copy. The server otherwise replays its own record of the conversation, which knows
 * nothing about anything done by hand in the manual builder, so a chat message after a manual
 * edit would quietly undo it. Reading live also means the flow need not be saved first, which
 * would be a strange thing to demand before letting someone ask for a change.
 *
 * Save/publish/send reuse exactly the hooks and services the manual builder uses (ADR-0006
 * background sync, `useAutoSave`/`useDirtyState`/`useFlowSyncStatus`, `SaveStatusControl`,
 * `SendToPhoneDialog`) so the two builders can never disagree about what "saved" means, there is
 * one flow record and one sync pipeline, just two ways of authoring it.
 *
 * `active` says whether THIS mode is the one currently on screen. The pane stays mounted (hidden,
 * not unmounted) when the user switches to the manual builder, so the conversation and its
 * `sessionId` survive the switch, but the manual builder's own auto-save is watching the same
 * flow, and running both loops at once would race two saves against each other. Auto-save here is
 * suspended while inactive; FlowBuilder does the same in reverse.
 */
/** A conversation that moved within this long opens as the live one; older ones are history. */
const CONTINUE_WITHIN_MS = 24 * 60 * 60 * 1000;

const AiBuilderPane = ({
  getCurrentFlowJson, onApply, onFlowSaved, active = true, request,
  draftScope = 'new', startBlank = false,
}) => {
  const { authenticatedFetch, user, isDemoMode } = useAuth();
  const userId = user?.id;
  const { selectedEnvironment } = useEnvironment();
  const { currentFlow, setCurrentFlow, createFlow, updateFlow, publishFlow, refreshFlow } = useFlow();
  const { isConnected } = useWhatsAppConnection();
  const envId = selectedEnvironment?.id;

  // The draft this pane keeps in the browser (utils/assistantDraft.js): a flow that has never
  // been saved lives under "new", a saved one under its id, so the conversation survives a reload
  // either way and a generated flow is never only in the tab.
  const draftId = currentFlow?.id ?? draftScope;

  const [messages, setMessages] = React.useState([]);
  const [sessionId, setSessionId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [startedAt, setStartedAt] = React.useState(null);
  const [progressNote, setProgressNote] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [flowJson, setFlowJson] = React.useState(() => getCurrentFlowJson());
  // Static or dynamic: the flow's own, shared with the canvas through currentFlow. The assistant
  // sets it from what it builds (a booking flow looks up live times, so it is dynamic) and says so.
  const [flowType, setFlowType] = React.useState(() => flowTypeOf(currentFlow, getCurrentFlowJson()));
  React.useEffect(() => {
    if (currentFlow?.flowType && currentFlow.flowType !== flowType) setFlowType(currentFlow.flowType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlow?.flowType]);
  // Which draft has been looked at; nothing is written back before its draft was read, or the
  // empty first render would overwrite what a reload was meant to bring back.
  const [hydratedDraft, setHydratedDraft] = React.useState(null);

  // The flow this conversation belongs to. The builder page stays mounted when the route moves
  // from one flow to another, so without this the pane carried the previous flow's chat, session
  // and history into the next flow, and a message sent there would have tied the old session to
  // the new flow (2026-09-18). A save moves the conversation from "new" to the flow's id without
  // a reset, because that is the same conversation.
  const conversationFor = React.useRef(draftId);
  const [sessions, setSessions] = React.useState([]);
  const [viewing, setViewing] = React.useState(null);
  const loadedHistoryFor = React.useRef(null);
  React.useEffect(() => {
    if (conversationFor.current === draftId) return;
    conversationFor.current = draftId;
    setMessages([]);
    setSessionId(null);
    setSessions([]);
    setViewing(null);
    setError(null);
    setHydratedDraft(null);
    loadedHistoryFor.current = null;
    setFlowJson(getCurrentFlowJson() || currentFlow?.flowJson || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // Restore once the environment is known. The contexts load after the first render on a full
  // reload, so reading the draft during that render found nothing (2026-09-18).
  React.useEffect(() => {
    if (!envId) return;
    const key = `${envId}:${draftId}`;
    if (hydratedDraft === key) return;
    // "Describe your flow" means a blank page. The draft exists so a reload does not lose work,
    // and restoring it here handed somebody yesterday's conversation and yesterday's flow when
    // they had just asked for a new one (2026-09-22). Checked before the read rather than undone
    // after it, so the old flow never reaches the canvas at all.
    if (startBlank && draftId === 'new') {
      clearDraft(envId, 'new', userId);
      setHydratedDraft(key);
      return;
    }
    const draft = loadDraft(envId, draftId, userId);
    if (draft && messages.length === 0 && !sessionId) {
      if (draft.messages?.length) setMessages(draft.messages);
      if (draft.sessionId) setSessionId(draft.sessionId);
      // A never-saved flow goes back onto the canvas too: the next message reads the flow from
      // there, and the manual builder must show the same thing this pane does.
      if (draftId === 'new' && draft.flowJson && !flowJson && !getCurrentFlowJson()) {
        setFlowJson(draft.flowJson);
        onApply(draft.flowJson);
      }
    }
    setHydratedDraft(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, draftId]);

  // Keep the draft current. Nothing is written while there is nothing to lose.
  React.useEffect(() => {
    if (!envId || hydratedDraft !== `${envId}:${draftId}`) return;
    if (messages.length === 0 && !flowJson) return;
    saveDraft(envId, draftId, { flowJson, messages, sessionId }, userId);
    // Once this conversation's flow has an id, the copy kept under "new" is the same conversation
    // and must not come back as a second, unsaved flow the next time the builder opens blank.
    // Seen in production on 2026-09-18, where a save that did not go through doSave left it behind.
    if (draftId !== 'new' && sessionId) {
      const stale = loadDraft(envId, 'new', userId);
      if (stale && stale.sessionId === sessionId) clearDraft(envId, 'new', userId);
    }
  }, [envId, draftId, hydratedDraft, flowJson, messages, sessionId, userId]);

  // The conversations behind a saved flow, from the server. A recent one (moved within the last
  // day) opens as the live conversation; older ones are history, read-only until continued, and
  // the first message then starts a fresh session. Only when the local draft brought nothing
  // back: the draft is newer than anything the server has.
  React.useEffect(() => {
    const flowId = currentFlow?.id;
    if (!envId || !flowId || hydratedDraft !== `${envId}:${draftId}` || loadedHistoryFor.current === flowId) return;
    loadedHistoryFor.current = flowId;
    let cancelled = false;
    (async () => {
      try {
        if (typeof flowGenerationService.listSessions !== 'function') return;
        const list = await flowGenerationService.listSessions(envId, flowId, authenticatedFetch);
        if (cancelled) return;
        setSessions(list);
        const latest = list[0];
        const recent = latest && Date.now() - new Date(latest.lastTurnAt).getTime() < CONTINUE_WITHIN_MS;
        if (recent && messages.length === 0 && !sessionId) {
          const transcript = await flowGenerationService.getSession(envId, flowId, latest.sessionId, authenticatedFetch);
          if (cancelled) return;
          setMessages(transcript.messages.map((m) => ({ role: m.role, text: m.text })));
          setSessionId(transcript.sessionId);
        }
      } catch {
        // History is a convenience; the builder works without it.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, currentFlow?.id, hydratedDraft, draftId]);

  const openSession = async (id) => {
    try {
      const transcript = await flowGenerationService.getSession(envId, currentFlow.id, id, authenticatedFetch);
      setViewing({ sessionId: transcript.sessionId, startedAt: transcript.startedAt, messages: transcript.messages.map((m) => ({ role: m.role, text: m.text })) });
    } catch (e) {
      setError(e.message);
    }
  };
  const continueSession = () => {
    if (!viewing) return;
    setMessages(viewing.messages);
    setSessionId(viewing.sessionId);
    setViewing(null);
  };
  const [assets, setAssets] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [sendToPhoneOpen, setSendToPhoneOpen] = React.useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useAutoSavePreference();

  React.useEffect(() => {
    if (!envId) return;
    flowAssetService.list(envId, authenticatedFetch).then(setAssets).catch(() => setAssets([]));
  }, [envId, authenticatedFetch]);

  /**
   * Take on a flow that arrives after this pane has mounted.
   *
   * The canvas is read through a ref, and a ref cannot announce itself, so the initial state
   * above is a snapshot of whatever happened to be there at mount. Opening a template mounts the
   * assistant first and finishes saving the flow a moment later, which left the assistant showing
   * its blank "what should your flow do?" for a flow that was already on the screen.
   *
   * Only when it is holding nothing. A canvas edited by hand is newer than the last loaded copy,
   * so adopting the loaded one over it would drop those edits out of the preview and out of the
   * next message to the model.
   */
  React.useEffect(() => {
    const loaded = currentFlow?.flowJson;
    if (!loaded || isBlankFlow(loaded)) return;
    setFlowJson((existing) => existing || loaded);
  }, [currentFlow?.flowJson]);

  /**
   * Pick up edits made by hand while this pane was hidden.
   *
   * The pane never unmounts any more (see the class doc), so without this the preview would go
   * stale the moment someone switches to the manual builder, edits the canvas, and switches back,
   * `flowJson` above only adopts a loaded flow when it is holding nothing. Re-reading the canvas
   * on the transition into view is safe specifically because it is gated on `!dirty`: an unsaved
   * change made HERE, in the assistant, is more current than the canvas and must not be clobbered.
   */
  // Mirrors `dirty` (declared below, after the save wiring) so this effect, which must run
  // BEFORE that point in the file to read naturally alongside the other mount-time effects,
  // can see its latest value without adding it to a dependency array that would otherwise
  // re-run the transition check on every unrelated edit.
  const dirtyRef = React.useRef(false);
  const wasActive = React.useRef(active);
  // Bumped when the pane adopts JSON that is already what the server holds, so auto-save and the
  // dirty check take it as the new baseline rather than as an edit. Without it, every switch back
  // from the canvas looked like a change: the pane saved the same flow again and WhatsApp re-synced.
  const [baselineGen, setBaselineGen] = React.useState(0);
  React.useEffect(() => {
    const becameActive = active && !wasActive.current;
    wasActive.current = active;
    if (!becameActive || dirtyRef.current) return;
    const fromCanvas = getCurrentFlowJson();
    if (!fromCanvas || jsonEquals(fromCanvas, flowJson)) return;
    setFlowJson(fromCanvas);
    // What the canvas holds is already saved: not an edit. Anything else is an unsaved change made
    // by hand, which the next auto-save should (and will) write, once.
    if (currentFlow?.flowJson && jsonEquals(fromCanvas, currentFlow.flowJson)) {
      setBaselineGen((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Background sync + validation status of the most recently saved flow (ADR-0006), the same
  // hook the manual builder uses, so "Synced" here means exactly what it means there.
  const { status: syncStatus, track: trackSync } = useFlowSyncStatus(2000, { active });
  React.useEffect(() => {
    trackSync(currentFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlow?.id, currentFlow?.updatedAt, trackSync]);

  // Tracks unsaved edits to the AI-produced flow. Baselines to whatever `flowJson` holds when a
  // given flow id is first seen, exactly like the manual builder's dirty check, a freshly loaded
  // or freshly generated flow is not "unsaved changes" until it is actually edited again.
  const { dirty, markClean } = useDirtyState(`${currentFlow?.id ?? 'new'}:${baselineGen}`, flowJson);
  dirtyRef.current = dirty;

  /** Change the flow's type, saving it at once when the flow exists (local metadata, no re-sync). */
  const handleFlowTypeChange = async (next) => {
    setFlowType(next);
    if (!currentFlow?.id) return;
    try {
      setCurrentFlow(await updateFlow(currentFlow.id, { flowType: next }));
    } catch (e) {
      setError(e.message || 'Could not change the flow type');
    }
  };

  const doSave = async (snapshot) => {
    if (!flowJson) return null;
    const name = currentFlow?.name || flowJson.screens?.[0]?.title || 'Untitled flow';
    const saved = currentFlow?.id
      ? await updateFlow(currentFlow.id, { name: currentFlow.name || name, flowJson, flowType, snapshot })
      : await createFlow({ name, flowJson, flowType });
    // The same conversation continues under the flow's id: not a flow change, no reset.
    if (!currentFlow?.id && saved?.id) conversationFor.current = saved.id;
    setCurrentFlow(saved);
    markClean();
    // Saved server-side now: the "new" draft has done its job, and the conversation carries on
    // under the flow's id from the next render. The conversation is tied to the flow on the
    // server too, so it is there for any browser that opens this flow later.
    if (!currentFlow?.id) {
      clearDraft(envId, 'new', userId);
      saveDraft(envId, saved.id, { flowJson, messages, sessionId }, userId);
    }
    if (sessionId && saved?.id) flowGenerationService.linkSession?.(envId, saved.id, sessionId, authenticatedFetch);
    if (onFlowSaved) onFlowSaved(saved);
    track('flow_saved', { isUpdate: !!currentFlow?.id, source: 'assistant', manual: !!snapshot });
    return saved;
  };

  // Explicit save, from the Save/Update button or the status chip: always version-snapshotted,
  // exactly like the manual builder's "Save now".
  const handleSaveNow = async () => {
    try {
      await doSave(true);
      clearAutoSaveError();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  // Debounced auto-save, only once the flow exists server-side (a brand-new flow needs an
  // explicit first save, same as the manual builder) and only while this mode is the one on
  // screen, so it can't race the manual builder's own auto-save over the same flow.
  const { error: autoSaveError, clearError: clearAutoSaveError } = useAutoSave(
    `${currentFlow?.id ?? 'new'}:${baselineGen}`,
    flowJson,
    () => doSave(false),
    {
      enabled: active && !!currentFlow?.id && autoSaveEnabled && isConnected,
      onError: (message) => setError(`Auto-save failed: ${message}`),
    },
  );

  const saveDisabledReason = !isConnected
    ? 'Connect a WhatsApp Business Account to save flows'
    : null;

  // Publishing means going live on a WhatsApp number, and the number has to be theirs. Said
  // here rather than left to a refusal from the server, because it is the next thing to do
  // rather than something withheld.
  const publishDisabledReason = isDemoMode
    ? 'Connect your WhatsApp number to publish this flow'
    : !currentFlow?.id
      ? 'Save the flow first'
      : currentFlow.status === 'PUBLISHED'
        ? 'Already published'
        : currentFlow.status !== 'DEPLOYED'
          ? 'Waiting for the flow to finish syncing to WhatsApp…'
          : null;

  const handlePublish = async () => {
    if (!currentFlow?.id) return;
    try {
      await publishFlow(currentFlow.id);
      await refreshFlow(currentFlow.id);
      track('flow_published', { flowId: currentFlow.id, source: 'assistant' });
      say('assistant', 'Published. This flow is now live on WhatsApp.');
    } catch (e) {
      setError(e.message || 'Publish failed');
    }
  };

  // Same gating as the manual builder's preview: the flow on Meta must already equal what's on
  // screen, so what WhatsApp draws is exactly what was just built.
  const liveSyncStatus = syncStatus?.syncStatus ?? currentFlow?.syncStatus;
  const liveValidationStatus = syncStatus?.validationStatus ?? currentFlow?.validationStatus;
  // Meta's own preview reads the flow back from WhatsApp by its real id, so it needs exactly
  // what a test send needs: saved, synced, and judged valid at the other end.
  const previewDisabledReason = !currentFlow?.id
    ? 'Save the flow to preview it'
    : !currentFlow?.whatsappFlowId
      ? 'Waiting for the flow to sync to WhatsApp…'
      : liveSyncStatus !== 'SYNCED'
        ? 'Waiting for validation to finish…'
        : liveValidationStatus === 'INVALID'
          ? 'Fix validation errors before previewing'
          : liveValidationStatus !== 'VALID'
            ? 'Waiting for validation to finish…'
            : null;

  /**
   * "Send it to my phone", from a flow that may not be saved yet.
   *
   * Saving first is this button's job, not the visitor's: they asked to see the form on their
   * phone, and "save it first" is a sentence about our data model. The dialog then waits out the
   * few seconds the flow takes to reach WhatsApp.
   */
  const handleSendToPhone = async () => {
    setError(null);
    try {
      if (!currentFlow?.id) await doSave(true);
      setSendToPhoneOpen(true);
    } catch (e) {
      setError(e.message || 'Could not get this flow onto a phone');
    }
  };

  // Offered to everybody (2026-10-15): a demo's flow is answered from FlowGate's shared number,
  // anybody else's from the number connected to this environment.
  const sendToPhoneDisabledReason = !flowJson
    ? 'Build a flow first'
    : !isDemoMode && !isConnected
      ? 'Connect a WhatsApp number to try it on your phone'
      : null;

  /**
   * Take images the customer already has, so the assistant can put their own pictures in.
   *
   * Uploaded before the next generation on purpose: the schema withholds the image components
   * unless a library exists, so a picture added afterwards is one the flow could never have
   * used.
   */
  const onUpload = async (files) => {
    setError(null);
    setUploading(true);
    try {
      const added = [];
      for (const file of files) {
        added.push(await flowAssetService.upload(envId, file, null, authenticatedFetch));
      }
      setAssets((previous) => [...previous, ...added]);
      const names = added.map((a) => a.description || a.filename).join(', ');
      say('assistant', added.length === 1
        ? `Got ${names}. Tell me where to use it, or ask me to work it in.`
        : `Got ${added.length} images: ${names}. Tell me where to use them.`);
      track('ai_flow_asset_uploaded', { count: added.length });
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onRemoveAsset = async (assetId) => {
    try {
      await flowAssetService.remove(envId, assetId, authenticatedFetch);
      setAssets((previous) => previous.filter((a) => a.id !== assetId));
    } catch (e) {
      setError(e.message);
    }
  };

  const say = (role, text, warnings, summary) =>
    setMessages((prev) => [...prev, { role, text, warnings, summary }]);

  /**
   * Empty the pane for a flow that has nothing to do with what was here.
   *
   * The restored draft is the thing being cleared. A brief arrives from outside (the landing
   * page) after this pane has already brought back whatever unsaved conversation the browser
   * was holding, and on 2026-09-19 that conversation simply swallowed the brief: ask for a dog
   * grooming form, get yesterday's bike shop quote. The draft is unsaved work, but the person
   * has just said, in as many words, that they want something else.
   */
  const startAfresh = (flowJson = null, name = undefined) => {
    setMessages([]);
    setSessionId(null);
    setSessions([]);
    setViewing(null);
    setError(null);
    setFlowJson(flowJson);
    // Through onApply so the canvas agrees with the preview; the manual builder owns the flow
    // this pane reads back on the next message.
    onApply(flowJson, name);
    clearDraft(envId, 'new', userId);
  };

  /**
   * Generation now runs as a background job; this is called on every poll with the job's own
   * real progress, so the pane shows what the server is actually doing rather than a guess from
   * an elapsed timer.
   */
  const onJobProgress = (job) => {
    if (job?.progressNote) setProgressNote(job.progressNote);
    // The draft so far: the planned pipeline sends the first screens within seconds and keeps
    // working on translations behind them, so the flow builds in front of the person.
    if (job?.preview) setFlowJson(job.preview);
  };

  /** What happens once a generate/refine job succeeds, shared with recovering one on mount. */
  const applyResult = (result) => {
    setSessionId(result.sessionId);
    // A turn on a saved flow is history of that flow from now on.
    if (currentFlow?.id && result.sessionId) {
      flowGenerationService.linkSession?.(envId, currentFlow.id, result.sessionId, authenticatedFetch);
    }
    // Null when it answered instead of building. Keep the flow that is already there rather
    // than blanking the preview because it said something.
    if (result.flowJson) {
      setFlowJson(result.flowJson);
      onApply(result.flowJson);
    }
    const builtType = result.flowJson ? inferFlowType(result.flowJson) : flowType;
    const said = result.message?.trim();
    if (said) {
      say('assistant', said, result.warnings);
    } else {
      // The model narrated nothing. Filling the bubble with a synthesized "Built N screens
      // with M questions" sentence used to make the conversation read like a status bar, the
      // same line with two numbers changed, presented as if the assistant had said it. Say
      // something honestly neutral instead, and put the structured counts where they visibly
      // are what they are: a caption, not narration.
      say('assistant', 'Here is your flow. Anything to change?', result.warnings, summarize(result));
    }
    if (builtType !== flowType) {
      handleFlowTypeChange(builtType);
      say('assistant', builtType === 'DYNAMIC'
        ? 'I have made this a dynamic flow: it looks up live information while the customer fills it in.'
        : 'This is now a static flow: everything the customer sees is in the flow itself.');
    }
    track('ai_flow_generated', {
      turn: result.turnIndex,
      outcome: result.outcome,
      repairTurns: result.repairTurns,
    });
    // Launch funnel event (docs/launch/ws-I.md): a flow was actually built, not just talked
    // about. No prompt text or flow content in the props, length only.
    if (result.flowJson) {
      markFlowBuilt();
      track('flow_generated', { turn: result.turnIndex, outcome: result.outcome });
    }
  };

  const run = async (text, call) => {
    setError(null);
    say('user', text);
    setBusy(true);
    setStartedAt(Date.now());
    setProgressNote(null);
    try {
      applyResult(await call());
    } catch (e) {
      setError(e.message);
      track('ai_flow_generation_failed', { message: e.message });
    } finally {
      setBusy(false);
      setStartedAt(null);
      setProgressNote(null);
    }
  };

  /**
   * Structured counts shown as a caption under the neutral fallback line above, never as if the
   * model said them, since it didn't.
   */
  const summarize = (result) => {
    const screens = result.flowJson?.screens || [];
    const fields = screens.reduce((total, screen) => total + countFields(screen.layout?.children), 0);
    const screenWord = screens.length === 1 ? 'screen' : 'screens';
    const fieldWord = fields === 1 ? 'question' : 'questions';
    return `${screens.length} ${screenWord} · ${fields} ${fieldWord}`;
  };

  const countFields = (children) => (children || []).reduce((total, child) => {
    const nested = ['children', 'then', 'else']
      .reduce((sum, key) => sum + (Array.isArray(child[key]) ? countFields(child[key]) : 0), 0);
    return total + (child?.name && child.type !== 'Footer' ? 1 : 0) + nested;
  }, 0);

  /**
   * @param options.fromScratch ignore whatever is on the canvas. A brief typed on the landing
   *   page asks for a new flow; sending the old one with it would turn "a dog grooming booking
   *   form" into an edit of somebody's half-finished bike shop quote.
   */
  const onGenerate = (prompt, { fromScratch = false } = {}) => {
    // Launch funnel event (docs/launch/ws-I.md): the top of the activation funnel, someone
    // described what they want. No prompt text in the props; it can carry business detail.
    markFirstBrief();
    track('flow_described', { promptLength: prompt?.length || 0 });
    return run(prompt, () =>
      flowGenerationService.generate(
        envId,
        // A flow already on the canvas makes this an edit of it, not a blank page. Without it
        // the first message would silently replace whatever was there.
        { prompt, useAssets: true, flowJson: fromScratch ? null : getCurrentFlowJson() },
        authenticatedFetch,
        onJobProgress,
      ));
  };

  const onRefine = (instruction) => run(instruction, () =>
    flowGenerationService.refine(
      envId,
      sessionId,
      { instruction, useAssets: true, flowJson: getCurrentFlowJson() },
      authenticatedFetch,
      onJobProgress,
    ));

  // What is wrong with the flow as it stands, computed exactly the way the manual builder does it:
  // one validation map over the same screens, so the two modes show the same count and the same
  // panel. It used to run a different validator here, which could disagree with the canvas.
  const { connected: liveDataConnected, refresh: refreshLiveData } = useLiveDataConnected(currentFlow?.id);
  const validationScreens = React.useMemo(() => {
    if (!flowJson || !Array.isArray(flowJson.screens) || flowJson.screens.length === 0) return [];
    try {
      return hydrate(flowJson);
    } catch {
      // A flow mid-generation can be momentarily unreadable; no problems is better than a crash.
      return [];
    }
  }, [flowJson]);
  const validationMap = React.useMemo(() => buildBuilderValidationMap(validationScreens, {
    hasEndpoint: !!currentFlow?.endpointUri || liveDataConnected || isBookingFlowJson(flowJson),
  }), [validationScreens, currentFlow?.endpointUri, liveDataConnected, flowJson]);
  const problems = React.useMemo(
    () => problemsFromValidationMap(validationMap, validationScreens, syncStatus),
    [validationMap, validationScreens, syncStatus]);
  const [validationPanelOpen, setValidationPanelOpen] = React.useState(false);

  // One message that asks for every problem to be fixed. A conversation continues it; a flow
  // built by hand (no session yet) starts one, with the canvas flow as the starting point.
  const onFixProblems = (list = problems) => {
    if (!list.length || busy) return;
    const text = fixRequestText(list);
    track('ai_fix_requested', { problems: list.length, fromSession: !!sessionId });
    return sessionId ? onRefine(text) : onGenerate(text);
  };

  /**
   * A request handed to this pane from outside it: the validation panel's "fix these"
   * ({@code problems}), or a brief the visitor typed before they ever reached the app
   * ({@code prompt}). Sent once per request id, and only once this pane is the one on screen and
   * nothing else is running.
   *
   * A brief waits for the draft to be read back first. Without that, a reload of a page that
   * still carried the brief would generate on top of the conversation the draft is about to
   * restore.
   */
  const [confirmFresh, setConfirmFresh] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [triggerOpen, setTriggerOpen] = React.useState(false);

  const lastRequestId = React.useRef(null);
  React.useEffect(() => {
    if (!request || request.id === lastRequestId.current || !active || busy) return;
    // A conversation we already know the answer to: the worked example the landing page offers
    // for "show me one". Put it on screen as though it had just happened, there is nothing to
    // generate, and from the next message on it is an ordinary flow being edited.
    if (request.transcript) {
      if (!envId || hydratedDraft !== `${envId}:${draftId}`) return;
      lastRequestId.current = request.id;
      if (messages.some((message) => message.text === request.transcript[0]?.text)) return;
      startAfresh(request.flowJson || null, request.name);
      setMessages(request.transcript);
      return;
    }
    if (request.prompt) {
      if (!envId || hydratedDraft !== `${envId}:${draftId}`) return;
      lastRequestId.current = request.id;
      // Already built from this exact brief, a reload of the page that carried it, where the
      // draft has since come back. Anything else is somebody asking for something new.
      if (messages.some((message) => message.role === 'user' && message.text === request.prompt)) {
        return;
      }
      startAfresh();
      onGenerate(request.prompt, { fromScratch: true });
      return;
    }
    lastRequestId.current = request.id;
    onFixProblems(request.problems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id, active, busy, envId, hydratedDraft, draftId]);

  /**
   * Pick a generation back up if the tab navigated away and back mid-job. Without this, a job
   * still running server-side when this pane remounts would simply be forgotten, the browser
   * moves on, and the flow it was building never comes back even though the server finished it.
   */
  React.useEffect(() => {
    if (!envId || typeof flowGenerationService.hasPendingJob !== 'function') return;
    if (!flowGenerationService.hasPendingJob(envId)) return;
    setError(null);
    setBusy(true);
    setStartedAt(Date.now());
    setProgressNote(null);
    flowGenerationService.resumePendingJob(envId, authenticatedFetch, onJobProgress)
      .then(applyResult)
      .catch((e) => {
        setError(e.message);
        track('ai_flow_generation_failed', { message: e.message });
      })
      .finally(() => {
        setBusy(false);
        setStartedAt(null);
        setProgressNote(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  return (
    <>
      <AiFlowBuilder
        flowJson={flowJson}
        sessionId={sessionId}
        messages={messages}
        busy={busy}
        startedAt={startedAt}
        progressNote={progressNote}
        error={error}
        onGenerate={onGenerate}
        onRefine={onRefine}
        assets={assets}
        uploading={uploading}
        onUpload={onUpload}
        onRemoveAsset={onRemoveAsset}
        assetUrl={(assetId) => flowAssetService.contentUrl(envId, assetId)}
        flow={currentFlow}
        dirty={dirty}
        autoSaveError={autoSaveError}
        syncStatus={syncStatus}
        autoSaveEnabled={autoSaveEnabled}
        onToggleAutoSave={setAutoSaveEnabled}
        onSaveNow={handleSaveNow}
        saveDisabledReason={saveDisabledReason}
        onPublish={handlePublish}
        publishDisabledReason={publishDisabledReason}
        onSendToPhone={handleSendToPhone}
        sendToPhoneDisabledReason={sendToPhoneDisabledReason}
        onOpenPreview={() => setPreviewOpen(true)}
        previewDisabledReason={previewDisabledReason}
        onOpenTriggers={() => setTriggerOpen(true)}
        triggersDisabledReason={currentFlow?.id ? null : 'Save the flow first'}
        validationMap={validationMap}
        validationScreens={validationScreens}
        validationMeta={syncStatus}
        onOpenValidation={() => setValidationPanelOpen(true)}
        onFixProblems={(list) => onFixProblems(list)}
        flowType={flowType}
        onFlowTypeChange={handleFlowTypeChange}
        sessions={sessions}
        currentSessionId={sessionId}
        viewing={viewing}
        onOpenSession={openSession}
        onCloseSession={() => setViewing(null)}
        onContinueSession={continueSession}
        onStartAfresh={messages.length > 0 || flowJson ? () => setConfirmFresh(true) : undefined}
      />
      <Dialog open={confirmFresh} onClose={() => setConfirmFresh(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start fresh?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This clears the conversation and the flow on the canvas. Anything already saved stays
            saved, only unsaved work goes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFresh(false)}>Cancel</Button>
          <Button
            variant="contained"
            data-testid="confirm-start-afresh"
            onClick={() => { setConfirmFresh(false); startAfresh(); }}
          >
            Start fresh
          </Button>
        </DialogActions>
      </Dialog>
      {/* The same two the manual builder offers, reached without leaving the conversation:
          WhatsApp's own rendering of the published flow, and the keyword that auto-sends it. */}
      <WhatsAppPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} flowId={currentFlow?.id} />
      <FlowConnectionsDrawer
        open={triggerOpen}
        onClose={() => { setTriggerOpen(false); refreshLiveData(); }}
        flow={currentFlow}
      />
      {/* The same panel the canvas opens from the same control, minus jumping to a component
          (there is no canvas here to jump on). */}
      <ValidationSummaryPanel
        open={validationPanelOpen}
        onClose={() => setValidationPanelOpen(false)}
        validationMap={validationMap}
        screens={validationScreens}
        meta={syncStatus}
        onFixWithAssistant={problems.length ? () => { setValidationPanelOpen(false); onFixProblems(problems); } : undefined}
      />
      <SendToPhoneDialog open={sendToPhoneOpen} onClose={() => setSendToPhoneOpen(false)} flow={currentFlow} />
    </>
  );
};

export default AiBuilderPane;
